import io, { Socket } from 'socket.io-client';
import { WordWithTimestamp } from '../utils/webSpeechAPI';
import { PerformanceLogger } from '../utils/performanceLogger';

export interface StreamingRecognitionResult {
  transcript: string;
  confidence: number;
  isFinal: boolean;
  stability?: number;
  words: Array<{
    word: string;
    startTime: number;
    endTime: number;
    confidence: number;
    speakerTag?: number;
  }>;
  languageCode?: string;
}

export interface PronunciationScore {
  wordIndex: number;
  word: string;
  pronunciationScore: number;
  confidence: number;
  feedback: string;
}

export class SpeechStreamingService {
  private socket: Socket | null = null;
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private isStreaming: boolean = false;
  private onResultCallback: ((result: StreamingRecognitionResult) => void) | null = null;
  private onScoresCallback: ((scores: PronunciationScore[]) => void) | null = null;
  private onErrorCallback: ((error: any) => void) | null = null;
  private audioChunkBuffer: Int16Array[] = [];
  private externalStream: boolean = false;
  private externalContext: boolean = false;
  private performanceLogger: PerformanceLogger;
  private heartbeatInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Determine backend URL based on environment
    const backendUrl = process.env.NODE_ENV === 'production' 
      ? 'https://voice-coach-backend-406125116283.us-east1.run.app'
      : 'http://localhost:8080';
    
    // Initialize Socket.IO connection with better settings
    this.socket = io(backendUrl, {
      transports: ['polling', 'websocket'],  // Start with polling, upgrade to websocket
      reconnection: true,
      reconnectionAttempts: Infinity,  // Keep trying to reconnect
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,  // 20 second connection timeout
      autoConnect: true,
      upgrade: true,  // Allow transport upgrades
      rememberUpgrade: true  // Remember the upgrade
    });

    this.performanceLogger = PerformanceLogger.getInstance();
    this.setupSocketListeners();
  }

  private setupSocketListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('Connected to speech streaming server');
      console.log('Transport type:', this.socket?.io.engine.transport.name);
      console.log('Socket ID:', this.socket?.id);
      
      // Start heartbeat to keep connection alive
      this.startHeartbeat();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Disconnected from speech streaming server. Reason:', reason);
      // Stop heartbeat
      this.stopHeartbeat();
      // Only stop streaming if it's an intentional disconnect
      if (reason === 'io server disconnect' || reason === 'io client disconnect') {
        this.stopStreaming();
      }
      // For other disconnects (network issues), socket.io will auto-reconnect
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('Reconnected to speech streaming server after', attemptNumber, 'attempts');
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('Attempting to reconnect... Attempt #', attemptNumber);
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('Reconnection error:', error.message);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('Failed to reconnect after all attempts');
      if (this.onErrorCallback) {
        this.onErrorCallback({ message: 'Connection lost. Please refresh the page.' });
      }
    });

    this.socket.on('recognition-started', () => {
      console.log('Server: Recognition started');
    });

    this.socket.on('recognition-ended', () => {
      console.log('Server: Recognition ended');
    });

    this.socket.on('recognition-result', (result: StreamingRecognitionResult) => {
      if (result.isFinal) {
        const duration = this.performanceLogger.endTiming('pronunciation-analysis-roundtrip');
        console.log(`[Performance] Pronunciation analysis round-trip took: ${duration.toFixed(3)} seconds.`);
      }
      if (this.onResultCallback) {
        this.onResultCallback(result);
      }
    });

    this.socket.on('pronunciation-scores', (scores: PronunciationScore[]) => {
      if (this.onScoresCallback) {
        this.onScoresCallback(scores);
      }
    });

    this.socket.on('recognition-error', (error: any) => {
      console.error('[SpeechStreaming] Recognition error from server:', error);
      if (this.onErrorCallback) {
        console.log('[SpeechStreaming] Triggering error callback');
        this.onErrorCallback(error);
      }
    });

    this.socket.on('error', (error: any) => {
      console.error('[SpeechStreaming] Socket error:', error);
      if (this.onErrorCallback) {
        console.log('[SpeechStreaming] Triggering error callback from socket error');
        this.onErrorCallback(error);
      }
    });
  }

  async startStreaming(
    onResult: (result: StreamingRecognitionResult) => void,
    onScores: (scores: PronunciationScore[]) => void,
    onError?: (error: any) => void,
    sampleRate: number = 48000,
    languageCode: string = 'en-US'
  ): Promise<boolean> {
    console.log('[SpeechStreaming] startStreaming called with language:', languageCode);
    
    // If already streaming, stop the previous stream first
    if (this.isStreaming) {
      console.log('[SpeechStreaming] Already streaming, stopping previous stream first');
      this.stopStreaming();
      // Wait a bit for the stream to properly close
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    this.onResultCallback = onResult;
    this.onScoresCallback = onScores;
    this.onErrorCallback = onError || null;

    try {
      // Check socket connection
      if (!this.socket) {
        console.error('[SpeechStreaming] Socket is null');
        return false;
      }
      
      if (!this.socket.connected) {
        console.log('[SpeechStreaming] Socket not connected, attempting to connect...');
        this.socket.connect();
        
        // Wait for connection
        let retries = 0;
        while (!this.socket.connected && retries < 10) {
          await new Promise(resolve => setTimeout(resolve, 200));
          retries++;
        }
        
        if (!this.socket.connected) {
          console.error('[SpeechStreaming] Failed to connect socket after retries');
          return false;
        }
      }
      
      console.log('[SpeechStreaming] Socket connected:', this.socket.connected);
      console.log('[SpeechStreaming] Socket ID:', this.socket.id);
      
      // Send configuration to server
      console.log('[SpeechStreaming] Emitting start-recognition with sampleRate:', sampleRate, 'language:', languageCode);
      this.performanceLogger.startTiming('pronunciation-analysis-roundtrip');
      this.socket.emit('start-recognition', {
        sampleRate: sampleRate,
        encoding: 'PCM16',
        languageCode: languageCode
      });

      this.isStreaming = true;
      this.audioChunkBuffer = [];
      console.log('[SpeechStreaming] Audio streaming started successfully');
      return true;
    } catch (error) {
      console.error('[SpeechStreaming] Error starting audio stream:', error);
      if (this.onErrorCallback) {
        console.log('[SpeechStreaming] Calling error callback');
        this.onErrorCallback(error);
      }
      return false;
    }
  }

  stopStreaming() {
    if (!this.isStreaming) {
      console.log('[SpeechStreaming] stopStreaming called but not streaming');
      return;
    }

    console.log('[SpeechStreaming] Stopping streaming...');
    this.isStreaming = false;

    // Send any remaining buffered audio
    if (this.audioChunkBuffer.length > 0 && this.socket && this.socket.connected) {
      const combinedLength = this.audioChunkBuffer.reduce((sum, chunk) => sum + chunk.length, 0);
      const combinedBuffer = new Int16Array(combinedLength);
      let offset = 0;
      
      for (const chunk of this.audioChunkBuffer) {
        combinedBuffer.set(chunk, offset);
        offset += chunk.length;
      }
      
      const uint8Array = new Uint8Array(combinedBuffer.buffer);
      let binaryString = '';
      for (let i = 0; i < uint8Array.length; i++) {
        binaryString += String.fromCharCode(uint8Array[i]);
      }
      const base64Audio = btoa(binaryString);
      this.socket.emit('audio-chunk', {
        audio: base64Audio,
        sampleRate: 48000
      });
      
      this.audioChunkBuffer = [];
    }

    // Notify server
    if (this.socket && this.socket.connected) {
      this.socket.emit('stop-recognition');
    }

    console.log('Audio streaming stopped');
  }

  disconnect() {
    this.stopStreaming();
    // Don't actually disconnect the socket for singleton pattern
    // Just stop the streaming
    // if (this.socket) {
    //   this.socket.disconnect();
    //   this.socket = null;
    // }
  }
  
  forceDisconnect() {
    // Use this method only when you really want to disconnect
    this.stopStreaming();
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  isConnected(): boolean {
    if (!this.socket) {
      return false;
    }
    
    // Check if currently connected
    if (this.socket.connected) {
      return true;
    }
    
    // If not connected but socket exists, try to connect
    // and check if it's in the process of connecting
    if (this.socket.disconnected) {
      this.socket.connect();
    }
    
    // Return true if connecting (not disconnected)
    return !this.socket.disconnected;
  }

  isCurrentlyStreaming(): boolean {
    return this.isStreaming;
  }

  // New method to send audio data directly
  sendAudioChunk(audioData: Float32Array, sampleRate: number) {
    if (!this.isStreaming || !this.socket || !this.socket.connected) {
      if (!this.isStreaming) {
        console.log('[SpeechStreaming] sendAudioChunk called but not streaming');
      }
      if (!this.socket?.connected) {
        console.log('[SpeechStreaming] sendAudioChunk called but socket not connected');
      }
      return;
    }

    // Convert Float32Array to Int16Array for transmission
    const outputData = new Int16Array(audioData.length);
    for (let i = 0; i < audioData.length; i++) {
      const s = Math.max(-1, Math.min(1, audioData[i]));
      outputData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }

    // Buffer audio chunks
    this.audioChunkBuffer.push(outputData);
    
    // Send buffered chunks every 100ms to reduce network overhead
    if (this.audioChunkBuffer.length >= 5) {
      const combinedLength = this.audioChunkBuffer.reduce((sum, chunk) => sum + chunk.length, 0);
      const combinedBuffer = new Int16Array(combinedLength);
      let offset = 0;
      
      for (const chunk of this.audioChunkBuffer) {
        combinedBuffer.set(chunk, offset);
        offset += chunk.length;
      }
      
      // Send audio chunk to server as base64
      const uint8Array = new Uint8Array(combinedBuffer.buffer);
      let binaryString = '';
      for (let i = 0; i < uint8Array.length; i++) {
        binaryString += String.fromCharCode(uint8Array[i]);
      }
      const base64Audio = btoa(binaryString);
      
      this.socket.emit('audio-chunk', {
        audio: base64Audio,
        sampleRate: sampleRate
      });
      
      this.audioChunkBuffer = [];
    }
  }

  private startHeartbeat() {
    // Stop any existing heartbeat
    this.stopHeartbeat();
    
    // Send a ping every 15 seconds to keep the connection alive
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.connected) {
        this.socket.emit('ping');
      }
    }, 15000);
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }
}

// Singleton instance
let speechStreamingInstance: SpeechStreamingService | null = null;

export const getSpeechStreamingService = (): SpeechStreamingService => {
  if (!speechStreamingInstance) {
    speechStreamingInstance = new SpeechStreamingService();
  }
  return speechStreamingInstance;
};